import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import BarChartAxis from '../../../components/ui/BarChartAxis'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import StackedBar from '../../../components/ui/StackedBar'
import { axisScale } from '../../../lib/axisScale'
import { formatPercent } from '../../../lib/number'
import { BUCKET_TONE, STATUS_TONE_COLOR_VAR } from '../../actions/types'
import type { PersonLoad } from '../types'

interface Props {
  people: PersonLoad[]
  /**
   * Ana durum adları — **donut'la aynı kaynak**: sunucunun `statusDistribution` dilimlerinden gelir
   * (`bucket` → `statusName`), o da statü kataloğundan çözülür. Anahtar `GROUP_PENDING` /
   * `GROUP_COMPLETED` / `GROUP_CLOSED`.
   *
   * <p><b>Neden prop:</b> kart bu adları önce kendi i18n anahtarlarında **sabit** tutuyordu
   * (`home.managerLoad.pending` = "Görev Bekleniyor" …). Katalog değiştiğinde donut değişip bu kart
   * eski adda kalıyordu — iki yüzey aynı durumu farklı adla gösteriyordu (kullanıcı bildirimi
   * 2026-08-26). Tek kaynak: katalog.
   *
   * <p>Dağılım boşken (hiç görev yok → sunucu dilim üretmez) sözlük boş gelir; o durumda eski i18n
   * anahtarları **yedek** olarak kullanılır, böylece boş panoda legend/başlık boş kalmaz.
   */
  groupNames: Record<string, string>
  /**
   * Alt tabloyu daraltan **kişi seçimi** (kullanıcı isteği 2026-09-11): seçili sicil ya da seçim yoksa
   * `null`. Kart bu seçimi yalnız gösterir — çubuklar ondan etkilenmez (seçim **alt tabloyadır**, karta
   * değil); aksi hâlde tıklanan kişi dışındakiler ekrandan düşerdi ve grafik karşılaştırma değerini
   * kaybederdi.
   */
  selectedPerson: string | null
  /** Bir kişiye tıklandı. Aynı kişiye yeniden tıklamanın "seçimi kaldır" olması ÇAĞIRANIN işidir. */
  onSelectPerson: (employeeId: string) => void
}

/**
 * Kaydırmasız gösterilen azami satır (kullanıcı isteği 2026-09-16: "14 kayıt sonrasında scroll
 * görünsün"). Bunun üstünde liste KENDİ İÇİNDE kaydırılır — satır DÜŞMEZ, yalnız görünen pencere
 * sabitlenir.
 *
 * <p><b>Neden burada, CSS'te değil:</b> sayı iki şeyi birden belirler — kaydırma sınıfının açılması
 * (burada) ve pencerenin yüksekliği (`home.css`, `--load-visible-rows`). İkisine ayrı ayrı yazılsaydı
 * biri değiştiğinde diğeri sessizce eski kalır ve liste ya 14'te kaydırırken 13 satır gösterir ya da
 * kaydırma hiç açılmazdı. Bu yüzden değer buradan satıra inline CSS değişkeni olarak geçirilir.
 */
const VISIBLE_ROWS = 14

/**
 * Serilerin çizim sırası ve rengi. Renk, donut/sütun grafiğiyle **aynı** kaynaktan (`BUCKET_TONE`) gelir:
 * bu kart, üstündeki donut'un kişi kırılımıdır ve iki grafik aynı ana durumu farklı renkte gösteremez.
 */
const SERIES = [
  { key: 'pending', group: 'GROUP_PENDING', of: (p: PersonLoad) => p.pendingCount },
  { key: 'completed', group: 'GROUP_COMPLETED', of: (p: PersonLoad) => p.completedCount },
  { key: 'closed', group: 'GROUP_CLOSED', of: (p: PersonLoad) => p.closedCount },
] as const

/**
 * "Yönetici Bazlı Aksiyon Beklenen, Tamamlanan ve Kapatılan Aksiyonlar" (086 — mockup 2026-08-13; 079'un
 * revizyonu). Her satır: ad → **üç** segmentli çubuk → satır toplamı; altta ortak sayı ekseni.
 *
 * <p><b>086'da değişen:</b> seriler yeni statü kataloğuna taşındı — Devam Eden / Güncellenen / Tamamlanan
 * yerine **Aksiyon Bekleniyor / Tamamlandı / Kapatıldı**.
 *
 * <p><b>Bar içi oranlar:</b> her segmentte adet + oran yazar (mockup). Oranın paydası <b>satırın kendi
 * toplamıdır</b> ("yöneticiye atanan tüm aksiyonlar") — panonun genel toplamı değil; iki yönetici böylece
 * kendi içlerindeki dağılımla karşılaştırılabilir. Metin <b>her</b> segmentte tam yazılır, oran dar
 * segmentte de düşmez (kullanıcı kararı 2026-08-17: "tüm barlarda yüzdelik değer de gözüksün"); bunu
 * {@link StackedBar} segmente metni kadar taban genişlik vererek sağlar. Bedeli, değeri küçük
 * segmentlerin olduğundan geniş çizilmesidir — grafik burada <b>sayıyı</b> okutmayı boy oranından üstün
 * tutar; gerçek ölçek satır sonundaki toplam ve alttaki eksenden doğrulanır (ayrıntı `ui.css`te).
 *
 * <p><b>Geciken ayrı segment değildir</b>: her ana durumun gecikmiş kırılımı kendi segmentinin içindedir
 * (donut'un altındaki sütun grafiği o kırılımı ayrı ayrı gösterir, "En Fazla Gecikme Yaşayan İlk 3
 * Yönetici" kartı da kişi bazında toplar). Bilgi yine de satırın erişilebilir açıklamasında durur →
 * ekran okuyucu kullanıcısı grafiğin çizmediği kırılımı duyar ve `lateCount` sözleşmeden düşmez.
 *
 * <p><b>Ortak ölçek:</b> tüm satırlar aynı `upper` değerine göre çizilir (eksenle birebir aynı) →
 * satırlar birbiriyle karşılaştırılabilir. Satır-içi normalize edilseydi 1/1 ile 50/50 aynı görünürdü.
 * Ölçek tabanı ham `max` değil, eksenin <b>yuvarlanmış</b> üst sınırıdır; aksi hâlde en uzun çubuk son
 * işareti taşardı.
 *
 * <p>Satır kümesini ve sırasını **sunucu** belirler (geciken ↓ → açık ↓ → tamamlanan ↓ → ad ↑);
 * istemci onu değiştirmez ve **kesmez**. **Satır sayısı sınırsızdır** (2026-09-10, kullanıcı kararı):
 * kişi filtresinde kaç kişi seçildiyse hepsi çizilir — eskiden 10'da kesiliyordu ve seçilen kişilerin
 * bir kısmı, hiçbir uyarı olmadan ekrandan düşüyordu.
 *
 * <p><b>Kaydırma (2026-09-16, kullanıcı isteği):</b> liste {@link VISIBLE_ROWS} satırdan uzunsa kendi
 * içinde dikey kaydırılır; kart artık seçime göre sınırsız uzamaz. Bu, 2026-09-10 kararını <b>geri
 * almaz</b> — o karar "hiçbir satır düşmesin" diyordu ve hâlâ geçerlidir: satırların tamamı DOM'dadır,
 * yalnız görünen pencere sabitlenir, kalanına kaydırarak erişilir. Kesme ile kaydırma farkı burada
 * esastır; 10'da kesme kişiyi <b>ulaşılamaz</b> kılıyordu.
 *
 * <p>Eksen bilerek kaydırma penceresinin <b>dışında</b> kalır: ortak ölçek her satır için geçerlidir,
 * satırlarla birlikte kaydırılsaydı okurken ekrandan çıkardı. Bunun bedeli, listeye çıkan kaydırma
 * çubuğunun çubuk rayını daraltıp ekseni hizadan kaydırmasıdır — telafisi aşağıda ölçülerek yapılır.
 */
export default function ManagerLoadCard({
  people,
  groupNames,
  selectedPerson,
  onSelectPerson,
}: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  const listRef = useRef<HTMLUListElement>(null)
  /** Kaydırma çubuğunun GERÇEK genişliği (px); eksen bu kadar sağdan geri hizalanır. Bkz. aşağıdaki not. */
  const [scrollbarWidth, setScrollbarWidth] = useState(0)

  const scrolls = people.length > VISIBLE_ROWS

  /**
   * Kaydırma çubuğu rayı daralttığı için eksenin hizası kayar (ölçüldü 2026-09-16: sağ hiza farkı
   * +10,0px). Genişlik tarayıcı/işletim sistemi ayarına bağlıdır — CSS'te sabit yazılamaz, bu yüzden
   * ölçülür. `useLayoutEffect`tir: boyama öncesi yazılmazsa eksen bir kare yanlış hizada görünür.
   */
  useLayoutEffect(() => {
    const el = listRef.current
    setScrollbarWidth(el != null && scrolls ? el.offsetWidth - el.clientWidth : 0)
  }, [scrolls])

  /** Katalog adı; yoksa (boş pano) eski sabit metne düşer. */
  const nameOf = (group: string, fallbackKey: string) =>
    groupNames[group] ?? t(`home.managerLoad.${fallbackKey}`)

  // Başlık da katalogdan beslenir (kullanıcı kararı 2026-08-26: "legend de başlık da DB'den gelsin").
  const title = t('home.managerLoad.title', {
    pending: nameOf('GROUP_PENDING', 'pending'),
    completed: nameOf('GROUP_COMPLETED', 'completed'),
    closed: nameOf('GROUP_CLOSED', 'closed'),
  })

  if (people.length === 0) {
    return (
      <SectionCard title={title}>
        <EmptyState
          icon="pi pi-users"
          title={t('home.personLoad.emptyTitle')}
          description={t('home.personLoad.emptyDesc')}
        />
      </SectionCard>
    )
  }

  const totalOf = (p: PersonLoad) => SERIES.reduce((acc, s) => acc + s.of(p), 0)
  const max = people.reduce((acc, p) => Math.max(acc, totalOf(p)), 0)
  const { upper } = axisScale(max)

  /** Segment metni: "adet (%oran)". Satır toplamı 0 iken oran tanımsızdır → yalnız adet yazılır. */
  const valueText = (value: number, total: number) =>
    total > 0 ? `${value} (%${formatPercent((value * 100) / total, i18n.language)})` : `${value}`

  const colorOf = (group: string) => STATUS_TONE_COLOR_VAR[BUCKET_TONE[group] ?? 'neutral']

  return (
    <SectionCard title={title} className="home-load">
      <div className="home-load-legend">
        {SERIES.map((series) => (
          <span key={series.key} className="home-load-key">
            <span
              className="home-load-swatch"
              style={{ background: colorOf(series.group) }}
              aria-hidden="true"
            />
            {nameOf(series.group, series.key)}
          </span>
        ))}
      </div>

      {/* Kaydırma penceresi: 14 satırdan uzun listelerde açılır. Sınıf KOŞULLU verilir — `max-height`
          her zaman uygulansaydı, satır yüksekliği tek pikselle bile şiştiğinde (font/tarayıcı farkı)
          tam 14 satırlık liste de kaydırma çubuğu gösterirdi. Kap ayrıca klavyeyle erişilebilirdir:
          her satırda gerçek bir `<button>` vardır, odak satıra geçtiğinde tarayıcı kabı kaydırır —
          bu yüzden `<ul>`ye ayrıca `tabindex` verilmez (verilirse liste gereksiz bir durak olurdu). */}
      <ul
        ref={listRef}
        className={scrolls ? 'home-load-list home-load-list--scroll' : 'home-load-list'}
        style={scrolls ? ({ '--load-visible-rows': VISIBLE_ROWS } as CSSProperties) : undefined}
        aria-label={title}
      >
        {people.map((p) => {
          const total = totalOf(p)
          // Erişilebilir açıklama gecikmeyi de söyler — grafik onu ayrı çizmese de bilgi kaybolmaz.
          const summary = t('home.personLoad.summary', {
            pending: p.pendingCount,
            late: p.lateCount,
            completed: p.completedCount,
            closed: p.closedCount,
          })
          const selected = selectedPerson === p.employeeId
          return (
            <li
              key={p.employeeId}
              className={selected ? 'home-load-row home-load-row--on' : 'home-load-row'}
            >
              {/* Ad gerçek bir `<button>`dur: klavye ve ekran okuyucu yolu burasıdır, `aria-pressed`
                  seçili kişiyi renkten bağımsız bildirir (donut legend'ıyla aynı desen). */}
              <button
                type="button"
                className="home-load-name home-load-name-btn"
                title={p.jobTitle ?? undefined}
                aria-pressed={selected}
                onClick={() => onSelectPerson(p.employeeId)}
              >
                {p.fullName}
              </button>
              <StackedBar
                className="home-load-bar"
                ariaLabel={summary}
                max={upper}
                showValues
                segments={SERIES.map((series) => ({
                  key: series.key,
                  label: t(`home.managerLoad.${series.key}`),
                  value: series.of(p),
                  color: colorOf(series.group),
                  valueText: valueText(series.of(p), total),
                }))}
              />
              <span className="home-load-total">{total}</span>
            </li>
          )
        })}
      </ul>

      <div
        className="home-load-axis"
        style={
          scrollbarWidth > 0
            ? ({ '--load-scrollbar-w': `${scrollbarWidth}px` } as CSSProperties)
            : undefined
        }
      >
        <BarChartAxis max={max} label={t('home.managerLoad.axis')} />
      </div>

      {/* Tıklanabilirlik yazıyla da söylenir — donut kartındaki yönergenin eşi. */}
      <p className="home-load-note">{t('home.personLoad.clickHint')}</p>
    </SectionCard>
  )
}
